import React, { useMemo, useState } from 'react';
import {
  ApiError,
  ChildDocumentKind,
  CreateCustomerRequestInput,
  CustomerRequest,
  CustomerRequestReason,
  CustomerRequestSource,
  DETAIL_FIELDS,
  DetailField,
  ProposedDetails,
  createCustomerRequest,
} from '../../api/customerRequests';
import {
  DETAIL_LABELS,
  MAX_OPTIONS,
  NOTE_MAX,
  OPTION_MAX,
  REASONS,
  reasonStarter,
} from './customerRequestCopy';
import '../FinancasOfficePicker/FinancasOfficePicker.css';
import './CustomerRequests.css';

export type CaseDetailValues = Partial<Record<DetailField, string | null | undefined>>;

interface Props {
  caseId: string;
  user: { getIdToken: () => Promise<string> } | null;
  /** The case's lawyer_status: a filed case asks on behalf of Finanças by default. */
  lawyerStatus?: string | null;
  applicantType?: string | null;
  documentType?: string | null;
  /** What the case holds today, the starting point for a `details` proposal. */
  caseValues: CaseDetailValues;
  /** "Ask again" opens on the reason and source of the request it follows. */
  initialReason?: CustomerRequestReason | null;
  initialSource?: CustomerRequestSource | null;
  onCancel: () => void;
  onCreated: (request: CustomerRequest) => void;
}

const startingDetails = (values: CaseDetailValues): Record<DetailField, string> =>
  DETAIL_FIELDS.reduce((acc, field) => {
    acc[field] = values[field] ?? '';
    return acc;
  }, {} as Record<DetailField, string>);

// Reasons the customer could never answer on this case (lawyer-backend refuses
// them too): a child's document on an adult case, an ID back without an ID card.
export const reasonUnavailable = (
  reason: CustomerRequestReason,
  applicantType?: string | null,
  documentType?: string | null
): string | null => {
  if (reason === 'child_document' && applicantType !== 'minor') return "Only on a child's application.";
  if (reason === 'id_back' && documentType !== 'id_card') return 'Only when the case was filed with an ID card.';
  return null;
};

// One request to the customer. The lawyer picks what is missing, writes the
// note the customer reads, and the backend sends the mail and moves the case
// to Waiting on customer.
const AskCustomerDialog: React.FC<Props> = ({
  caseId,
  user,
  lawyerStatus,
  applicantType,
  documentType,
  caseValues,
  initialReason = null,
  initialSource = null,
  onCancel,
  onCreated,
}) => {
  const [reason, setReason] = useState<CustomerRequestReason | null>(initialReason);
  const [fromFinancas, setFromFinancas] = useState<boolean>(
    initialSource ? initialSource === 'financas' : lawyerStatus === 'Submitted'
  );
  const [note, setNote] = useState<string>(reasonStarter(initialReason));
  const [options, setOptions] = useState<string[]>([]);
  const original = useMemo(() => startingDetails(caseValues), [caseValues]);
  const [details, setDetails] = useState<Record<DetailField, string>>(original);
  const [childDocument, setChildDocument] = useState<ChildDocumentKind>('birth_certificate');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const selectReason = (next: CustomerRequestReason) => {
    // Swap in the new starter unless the lawyer has already written her own.
    if (!note.trim() || note === reasonStarter(reason)) {
      setNote(reasonStarter(next));
    }
    setReason(next);
    setError('');
  };

  const changedDetails = DETAIL_FIELDS.filter(
    field => details[field].trim() !== original[field].trim()
  );

  const buildInput = (): CreateCustomerRequestInput | null => {
    if (!reason) return null;
    const input: CreateCustomerRequestInput = {
      reason,
      note: note.trim(),
      source: fromFinancas ? 'financas' : 'lawyer',
    };
    if (reason === 'question') {
      const choices = options.map(option => option.trim()).filter(Boolean);
      if (choices.length > 0) input.options = choices;
    }
    if (reason === 'details' && changedDetails.length > 0) {
      const proposed: ProposedDetails = {};
      changedDetails.forEach(field => {
        proposed[field] = details[field].trim();
      });
      input.proposed = proposed;
    }
    if (reason === 'child_document') {
      input.child_document = childDocument;
    }
    return input;
  };

  const canSend = Boolean(reason) && note.trim().length > 0 && note.length <= NOTE_MAX && !sending;

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    const input = buildInput();
    if (!input || !canSend) return;
    if (!user) {
      setError('You are signed out.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const token = await user.getIdToken();
      const created = await createCustomerRequest(token, caseId, input);
      onCreated(created);
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error ? err.message : 'Failed to send the request.'
      );
      setSending(false);
    }
  };

  const updateOption = (index: number, value: string) =>
    setOptions(prev => prev.map((option, i) => (i === index ? value : option)));

  return (
    <div
      className="financas-dialog-backdrop"
      onKeyDown={event => {
        if (event.key === 'Escape' && !sending) onCancel();
      }}
    >
      <form
        className="financas-dialog ask-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ask-dialog-title"
        onSubmit={handleSend}
      >
        <h3 id="ask-dialog-title">Ask the customer</h3>

        <fieldset className="ask-reasons">
          <legend>What is missing?</legend>
          {REASONS.map(copy => {
            const unavailable = reasonUnavailable(copy.reason, applicantType, documentType);
            return (
              <label
                key={copy.reason}
                className={`ask-reason${reason === copy.reason ? ' ask-reason--selected' : ''}${
                  unavailable ? ' ask-reason--unavailable' : ''
                }`}
              >
                <input
                  type="radio"
                  name="ask-reason"
                  value={copy.reason}
                  checked={reason === copy.reason}
                  onChange={() => selectReason(copy.reason)}
                  disabled={sending || Boolean(unavailable)}
                />
                <span className="ask-reason-text">
                  <span className="ask-reason-label">{copy.label}</span>
                  <span className="ask-reason-description">{unavailable || copy.description}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <label className="ask-check">
          <input
            type="checkbox"
            checked={fromFinancas}
            onChange={event => setFromFinancas(event.target.checked)}
            disabled={sending}
          />
          Finanças asked for this (the case is filed)
        </label>

        {reason === 'question' && (
          <div className="ask-extra">
            <span className="ask-extra-title">Answer choices (optional)</span>
            {options.map((option, index) => (
              <div key={index} className="ask-option-row">
                <input
                  type="text"
                  className="ask-input"
                  aria-label={`Choice ${index + 1}`}
                  maxLength={OPTION_MAX}
                  value={option}
                  onChange={event => updateOption(index, event.target.value)}
                  disabled={sending}
                />
                <button
                  type="button"
                  className="financas-btn financas-btn--link"
                  onClick={() => setOptions(prev => prev.filter((_, i) => i !== index))}
                  disabled={sending}
                  aria-label={`Remove choice ${index + 1}`}
                >
                  Remove
                </button>
              </div>
            ))}
            {options.length < MAX_OPTIONS && (
              <button
                type="button"
                className="financas-btn financas-btn--link ask-add-option"
                onClick={() => setOptions(prev => [...prev, ''])}
                disabled={sending}
              >
                Add a choice
              </button>
            )}
            <span className="ask-hint">Without choices the customer answers in their own words.</span>
          </div>
        )}

        {reason === 'details' && (
          <div className="ask-extra">
            <span className="ask-extra-title">Proposed details</span>
            <span className="ask-hint">
              Correct what is wrong. Only the values you change are sent as a proposal.
            </span>
            <div className="ask-details-grid">
              {DETAIL_FIELDS.map(field => (
                <label key={field} className="ask-details-field">
                  <span>
                    {DETAIL_LABELS[field]}
                    {changedDetails.includes(field) && <span className="ask-changed"> changed</span>}
                  </span>
                  <input
                    type="text"
                    className="ask-input"
                    value={details[field]}
                    onChange={event => setDetails(prev => ({ ...prev, [field]: event.target.value }))}
                    disabled={sending}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        {reason === 'child_document' && (
          <fieldset className="ask-extra ask-child">
            <legend className="ask-extra-title">Which document?</legend>
            <label className="ask-check">
              <input
                type="radio"
                name="ask-child-document"
                checked={childDocument === 'birth_certificate'}
                onChange={() => setChildDocument('birth_certificate')}
                disabled={sending}
              />
              Birth certificate
            </label>
            <label className="ask-check">
              <input
                type="radio"
                name="ask-child-document"
                checked={childDocument === 'child_id'}
                onChange={() => setChildDocument('child_id')}
                disabled={sending}
              />
              The child's ID
            </label>
          </fieldset>
        )}

        <label className="ask-note">
          <span className="ask-extra-title">Your note to the customer</span>
          <textarea
            className="ask-input ask-textarea"
            aria-label="Your note to the customer"
            rows={5}
            maxLength={NOTE_MAX}
            value={note}
            onChange={event => setNote(event.target.value)}
            disabled={sending}
            placeholder={reason ? 'Write what you need and why, in plain words.' : 'Pick what is missing first.'}
            required
          />
          <span className="ask-counter">{note.length} / {NOTE_MAX}</span>
        </label>

        <p className="ask-preview">
          The customer gets an email in their language with your note, and a page to answer on. The case
          moves to Waiting on customer.
        </p>

        {error && (
          <div className="status-error-banner" role="alert">{error}</div>
        )}

        <div className="financas-dialog-actions">
          <button type="button" className="financas-btn" onClick={onCancel} disabled={sending}>
            Cancel
          </button>
          <button type="submit" className="financas-btn financas-btn--primary" disabled={!canSend}>
            {sending ? 'Sending...' : 'Send to the customer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AskCustomerDialog;
